import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { CalendarCheck } from 'lucide-react';
import { useNotificationInbox } from '@/stores/useNotificationInbox';
import type { InboxNotification } from '@/types/NotificationTypes';
import { CelebrationMoment, hasCelebrationMoment } from '@/features/celebrations/CelebrationMoment';

/**
 * Full-screen takeover for `category: 'celebration'` notifications — the four
 * journey moments designed in the .pen (breakthrough, exam-ready, PASSED,
 * momentum). The backend's milestone ledger guarantees each fires once ever
 * (see aswb-backend src/notifications/milestones.ts), so this renders the
 * oldest unread celebration and marks it read on any dismissal path.
 *
 * Deliberately NOT an inbox row: the inbox shows history; this is the moment.
 */

type MilestoneVisual = {
  /** The big glyph in the gold badge */
  glyph: React.ReactNode;
  /** Eyebrow label above the title */
  eyebrow: string;
};

const VISUALS: Record<string, MilestoneVisual> = {
  first_quiz: { glyph: '🌱', eyebrow: 'First step' },
  diagnostic_done: { glyph: '🧭', eyebrow: 'You have a map' },
  streak_7: { glyph: '🔥', eyebrow: 'Day streak' },
  breakthrough: { glyph: '📈', eyebrow: 'Breakthrough' },
  exam_ready: { glyph: '🎯', eyebrow: 'Readiness' },
  // Not 📅: Apple renders that emoji as a fixed "JUL 17" page, a wrong date on every other exam date.
  exam_scheduled: { glyph: <CalendarCheck className="h-10 w-10 text-celebrate-700" strokeWidth={2.25} />, eyebrow: 'It is real now' },
  exam_taken: { glyph: '🏁', eyebrow: 'Exam day, done' },
  passed: { glyph: '🏆', eyebrow: 'The summit' },
};

const DEFAULT_VISUAL: MilestoneVisual = { glyph: '⭐', eyebrow: 'Milestone' };

const ONBOARDING_EXAM_SET_KEY = 'swtpa_onboarding_exam_set';

/** Past this the flag is stale: the dashboard landing it was armed for has come and gone. */
const ONBOARDING_EXAM_SET_TTL_MS = 5 * 60 * 1000;

/**
 * Onboarding just wrote a future exam date, so the once-ever exam_scheduled
 * moment would only restate it over the day-1 dashboard. Call right before
 * leaving onboarding with the date it wrote; without one, nothing is armed and
 * a date set later still celebrates.
 */
export function skipExamScheduledCelebrationFromOnboarding(writtenExamDate: string | undefined): void {
  if (!writtenExamDate) return;
  try {
    sessionStorage.setItem(ONBOARDING_EXAM_SET_KEY, String(Date.now()));
  } catch {
    // private mode: the celebration shows, which is the old behavior
  }
}

function onboardingExamSetPending(): boolean {
  try {
    const armedAt = Number(sessionStorage.getItem(ONBOARDING_EXAM_SET_KEY));
    return armedAt > 0 && Date.now() - armedAt < ONBOARDING_EXAM_SET_TTL_MS;
  } catch {
    return false;
  }
}

const milestoneOf = (n: InboxNotification): string => String(n.metadata?.milestone ?? '');

function fireConfetti() {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  // Two staggered bursts from the card's flanks — same idiom as the diagnostic.
  const base = { spread: 70, ticks: 200, gravity: 0.9, zIndex: 10000 };
  confetti({ ...base, particleCount: 90, origin: { x: 0.2, y: 0.65 }, angle: 60 });
  confetti({ ...base, particleCount: 90, origin: { x: 0.8, y: 0.65 }, angle: 120 });
}

const CelebrationOverlay: React.FC = () => {
  const navigate = useNavigate();
  const notifications = useNotificationInbox((s) => s.notifications);
  const markAsRead = useNotificationInbox((s) => s.markAsRead);

  // Oldest unread celebration first — journey order, not recency order.
  const celebration: InboxNotification | undefined = useMemo(
    () =>
      [...notifications]
        .filter((n) => n.category === 'celebration' && !n.read)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0],
    [notifications]
  );

  const skipThis = celebration !== undefined && milestoneOf(celebration) === 'exam_scheduled'
    && onboardingExamSetPending();

  useEffect(() => {
    if (!celebration || !skipThis) return;
    try {
      sessionStorage.removeItem(ONBOARDING_EXAM_SET_KEY);
    } catch {
      // unreadable storage already reads as "not pending"
    }
    void markAsRead(celebration.id);
  }, [celebration, skipThis, markAsRead]);

  // One confetti burst per shown notification id.
  const celebratedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (celebration && !skipThis && celebratedIdRef.current !== celebration.id) {
      celebratedIdRef.current = celebration.id;
      fireConfetti();
    }
  }, [celebration, skipThis]);

  useEffect(() => {
    if (!celebration || skipThis) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void markAsRead(celebration.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [celebration, skipThis, markAsRead]);

  if (!celebration || skipThis) return null;

  const visual = VISUALS[milestoneOf(celebration)] ?? DEFAULT_VISUAL;
  // Backend-owned kind: prefer the embedded catalog payload (backend #138),
  // fall back to the milestone ledger id for notifications created before it.
  const momentKind = String(
    (celebration as { celebration?: { kind?: string } }).celebration?.kind
      ?? celebration.metadata?.milestone
      ?? ''
  );
  const deltaChip = typeof celebration.metadata?.delta === 'string' ? celebration.metadata.delta : undefined;

  const dismiss = () => void markAsRead(celebration.id);
  const follow = () => {
    void markAsRead(celebration.id);
    if (celebration.actionUrl) navigate(celebration.actionUrl);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="celebration-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-neutral-900/60"
    >
      <div
        className="max-w-md w-full rounded-2xl border-2 border-neutral-200 bg-white p-8 text-center animate-fade-in"
        style={{ boxShadow: '0 8px 0 rgba(17,17,17,0.12)' }}
      >
        {/* The moment: per-kind animation from the celebration catalog when we
            know the kind; the gold glyph badge stays as the fallback for kinds
            the animation map hasn't learned (backend can ship moments early). */}
        {hasCelebrationMoment(momentKind) ? (
          <div className="mx-auto mb-5 flex justify-center" aria-hidden="true">
            <CelebrationMoment kind={momentKind} delta={deltaChip} />
          </div>
        ) : (
          <div
            className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border-2 border-celebrate-600 bg-celebrate-100"
            style={{ boxShadow: '0 5px 0 rgba(17,17,17,0.10)' }}
            aria-hidden="true"
          >
            <span className="text-4xl leading-none">{visual.glyph}</span>
          </div>
        )}

        <p className="eyebrow text-celebrate-700 mb-2">{visual.eyebrow}</p>
        <h1 id="celebration-title" className="font-display font-bold text-neutral-900 text-3xl leading-tight mb-3">
          {celebration.title}
        </h1>
        <p className="text-neutral-600 leading-relaxed mb-7">{celebration.message}</p>

        <button
          type="button"
          onClick={follow}
          className="block w-full rounded-xl bg-primary-600 px-4 py-3.5 text-lg font-bold text-white transition-colors hover:bg-primary-700"
          style={{ boxShadow: '0 4px 0 rgba(17,17,17,0.18)' }}
        >
          {celebration.actionLabel || 'Keep going'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="mt-3 w-full py-2 text-sm font-bold text-neutral-400 transition-colors hover:text-neutral-600"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default CelebrationOverlay;
